import React, { useRef, useState } from "react";
import ActionBar from "../../common/ActionBar";
import _ from "lodash";
import { Padding } from "../MyParties";
import { useSnackbar } from "notistack";
import queryString from "query-string";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import styled from "styled-components";
import { navigate } from "@reach/router";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@material-ui/core";
import { Visibility, GetApp } from "@material-ui/icons";
import { Bold } from "../../common/PartyTable";
import { getErrorMessage } from "../../../helpers/functions";
import CustomTable from "../../common/CustomTable/CustomTable";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCTAButton,
} from "../../common/Buttons";
import useLoader from "../../../hooks/useLoader";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
// same rows the backend makes bold: the 4 summary rows and the header
const BOLD_ROWS = [0, 1, 2, 3, 6];

/**
 * Delivery logs page shared by WhatsApp and SMS: one row per Schedule Hearing
 * trigger, with an Excel delivery report built by the backend.
 */
const DeliveryLogs = ({ title, getLogs, getReport }) => {
  const breadcrumbs = ["Comms Manager", title];
  const [state, setState] = useState();
  const [preview, setPreview] = useState(null);
  const MTRef = useRef();
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();

  const onBreadcrumbClick = (selected) => {
    switch (_.snakeCase(selected)) {
      case "comms_manager":
        navigate("/dashboard/case-manager/comms-manager");
        break;
      default:
        break;
    }
  };

  // styled workbook is built by the backend and sent as base64
  const fetchReport = async (rowData, message) => {
    setLoader({ state: true, message });
    try {
      const res = await getReport(rowData.id);
      const { fileName, file } = res?.data || {};
      if (!file) throw new Error("Report file was not generated");
      return { fileName: fileName || "delivery_report.xlsx", file };
    } finally {
      setLoader({ state: false });
    }
  };

  const downloadReport = ({ fileName, file }) => {
    const bytes = Uint8Array.from(atob(file), (char) => char.charCodeAt(0));
    saveAs(new Blob([bytes], { type: XLSX_MIME }), fileName);
  };

  const generateExcel = async (rowData) => {
    try {
      downloadReport(await fetchReport(rowData, "Generating Excel..."));
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    }
  };

  const viewExcel = async (rowData) => {
    try {
      const report = await fetchReport(rowData, "Loading report...");
      const workbook = XLSX.read(report.file, { type: "base64" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: "",
        blankrows: true,
      });
      setPreview({ ...report, title: rowData.details, rows });
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    }
  };

  const data = (query) =>
    new Promise((resolve) => {
      const params = { page: query.page + 1, perPage: 10 };
      if (query.search) params.search = query.search;

      getLogs(`?${queryString.stringify(params)}`)
        .then((res) => {
          const result = res?.data || {};
          setState(result);
          resolve({
            data: result.data || [],
            page: (result.page || 1) - 1,
            total: result.total || 0,
          });
        })
        .catch((error) => {
          enqueueSnackbar(getErrorMessage(error), { variant: "error" });
          setState({ data: [], lastPage: 1, page: 1, perPage: 10, total: 0 });
          resolve({ data: [], page: 0, total: 0 });
        });
    });

  const columns = [
    {
      field: "details",
      title: "Details",
      sorting: false,
      render: (rowData) => rowData.details,
      headerStyle: {
        paddingLeft: 35,
      },
    },
    {
      field: "category",
      title: "Category",
      sorting: false,
      render: (rowData) => rowData.category,
    },
    {
      field: "sentBy",
      title: "Send By",
      sorting: false,
      render: (rowData) => rowData.sentBy,
    },
    {
      field: "totalCases",
      title: "Total",
      sorting: false,
      render: (rowData) => (
        <Bold>
          {rowData.totalCases} {rowData.totalCases === 1 ? "Case" : "Cases"}
        </Bold>
      ),
    },
    {
      field: "report",
      title: "Report",
      sorting: false,
      render: (rowData) => (
        <ReportActions>
          <Tooltip title="Preview the report" arrow>
            <ReportButton type="button" onClick={() => viewExcel(rowData)}>
              <Visibility />
              View
            </ReportButton>
          </Tooltip>
          <Tooltip title="Download as Excel" arrow>
            <ReportButton
              type="button"
              $primary
              onClick={() => generateExcel(rowData)}
            >
              <GetApp />
              Excel
            </ReportButton>
          </Tooltip>
        </ReportActions>
      ),
    },
  ];

  return (
    <div>
      <ActionBar {...{ breadcrumbs, onBreadcrumbClick }} />
      <Padding>
        <CustomTable
          hideFilterBar
          hidePagination={state?.lastPage <= 1}
          pageSize={state?.data?.length ? state?.data?.length : 10}
          pluralTitle={title}
          placeholderText="Search Claimant Party Id"
          searchWidth="320px"
          singularTitle=""
          {...{
            columns,
            data,
            MTRef,
          }}
          state={state}
        />
      </Padding>

      <Dialog
        open={!!preview}
        onClose={() => setPreview(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{preview?.title}</DialogTitle>
        <DialogContent dividers>
          <SheetWrapper>
            <Sheet>
              <tbody>
                {(preview?.rows || []).map((row, rowIndex) => (
                  <tr key={rowIndex} className={BOLD_ROWS.includes(rowIndex) ? "bold" : ""}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex}>{String(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Sheet>
          </SheetWrapper>
        </DialogContent>
        <DialogActions>
          <PrimaryOutlinedCTAButton
            style={{ width: "auto", padding: "0 20px" }}
            onClick={() => setPreview(null)}
          >
            Close
          </PrimaryOutlinedCTAButton>
          <PrimaryCTAButton
            style={{ width: "auto", padding: "0 20px" }}
            onClick={() => downloadReport(preview)}
          >
            Download Excel
          </PrimaryCTAButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DeliveryLogs;

const ReportActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
`;

const ReportButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1.5px solid ${COLORS.BTN_GREEN};
  background: ${(p) => (p.$primary ? COLORS.BTN_GREEN : "#fff")};
  color: ${(p) => (p.$primary ? "#fff" : COLORS.BTN_GREEN)};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.15s ease;
  svg {
    font-size: 17px;
  }
  &:hover {
    background: ${(p) => (p.$primary ? "#006b72" : `${COLORS.BTN_GREEN}12`)};
    box-shadow: 0 2px 6px rgba(0, 131, 140, 0.25);
  }
`;

const SheetWrapper = styled.div`
  max-height: 65vh;
  overflow: auto;
`;

const Sheet = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13px;
  td {
    border: 1px solid #e1e3ee;
    padding: 6px 10px;
    white-space: nowrap;
    min-width: 60px;
  }
  tr.bold td {
    font-family: ${theme.fonts.primaryFontBold};
    font-weight: 700;
  }
`;
